/**
 * Database Migration Script for MOMM SQLite Enhancement
 * This script migrates the existing MOMM database to the new enhanced schema
 */

// Database migration functions
export const migrateMommDatabase = async (db) => {
  console.log('Starting MOMM database migration...');
  
  try {
    // Run migration without transactions for better error handling
    // DDL operations (CREATE TABLE, ALTER TABLE) don't need transactions and can cause issues

    // 1. First ensure base tables exist
    const baseTables = `
      CREATE TABLE IF NOT EXISTS mommChats (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        timestamp INTEGER NOT NULL,
        messages TEXT NOT NULL DEFAULT '[]',
        title TEXT DEFAULT 'New Chat'
      );
      
      CREATE TABLE IF NOT EXISTS mommMessages (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        message_id TEXT NOT NULL,
        sender TEXT NOT NULL,
        role TEXT NOT NULL,
        text TEXT,
        timestamp INTEGER NOT NULL,
        has_media BOOLEAN DEFAULT 0,
        media_urls TEXT,
        media_names TEXT,
        metadata TEXT,
        chat_id INTEGER
      );
      
      CREATE TABLE IF NOT EXISTS mommImages (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        hash TEXT UNIQUE NOT NULL,
        imageData TEXT NOT NULL,
        prompt TEXT,
        timestamp INTEGER NOT NULL,
        chat_id INTEGER,
        message_id TEXT,
        image_name TEXT,
        image_order INTEGER DEFAULT 0
      );
      
      CREATE TABLE IF NOT EXISTS mommAudio (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        hash TEXT UNIQUE NOT NULL,
        audioData TEXT NOT NULL,
        transcript TEXT,
        timestamp INTEGER NOT NULL
      );
      
      CREATE TABLE IF NOT EXISTS mommVideos (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        hash TEXT UNIQUE NOT NULL,
        videoData TEXT NOT NULL,
        prompt TEXT,
        timestamp INTEGER NOT NULL
      );
    `;
    
    // Create base tables first
    await new Promise((resolve, reject) => {
      db.exec(baseTables, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
    
    // 2. Now add enhancement columns (these will only add if they don't exist)
    const enhancedSchema = `
      ALTER TABLE mommChats ADD COLUMN created_at DATETIME DEFAULT CURRENT_TIMESTAMP;
      ALTER TABLE mommChats ADD COLUMN updated_at DATETIME DEFAULT CURRENT_TIMESTAMP;
      ALTER TABLE mommChats ADD COLUMN message_count INTEGER DEFAULT 0;
      ALTER TABLE mommChats ADD COLUMN metadata TEXT;
    `;

    const enhancedImageSchema = `
      -- Enhanced mommImages table (add collection support)
      ALTER TABLE mommImages ADD COLUMN file_size INTEGER;
      ALTER TABLE mommImages ADD COLUMN dimensions TEXT;
      ALTER TABLE mommImages ADD COLUMN is_collected BOOLEAN DEFAULT FALSE;
      ALTER TABLE mommImages ADD COLUMN tags TEXT;
      ALTER TABLE mommImages ADD COLUMN metadata TEXT;
    `;

    const enhancedAudioSchema = `
      -- Enhanced mommAudio table
      ALTER TABLE mommAudio ADD COLUMN chat_id INTEGER;
      ALTER TABLE mommAudio ADD COLUMN message_id TEXT;
      ALTER TABLE mommAudio ADD COLUMN duration REAL;
      ALTER TABLE mommAudio ADD COLUMN file_size INTEGER;
      ALTER TABLE mommAudio ADD COLUMN metadata TEXT;
    `;

    const enhancedVideoSchema = `
      -- Enhanced mommVideos table
      ALTER TABLE mommVideos ADD COLUMN chat_id INTEGER;
      ALTER TABLE mommVideos ADD COLUMN message_id TEXT;
      ALTER TABLE mommVideos ADD COLUMN duration REAL;
      ALTER TABLE mommVideos ADD COLUMN file_size INTEGER;
      ALTER TABLE mommVideos ADD COLUMN dimensions TEXT;
      ALTER TABLE mommVideos ADD COLUMN metadata TEXT;
      ALTER TABLE mommVideos ADD COLUMN generation_id TEXT;
    `;

    const newTables = `
      -- New collections table
      CREATE TABLE IF NOT EXISTS mommCollections (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT UNIQUE NOT NULL,
        description TEXT,
        created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
        updated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
        image_count INTEGER DEFAULT 0,
        thumbnail_image_id INTEGER,
        metadata TEXT,
        FOREIGN KEY (thumbnail_image_id) REFERENCES mommImages(id) ON DELETE SET NULL
      );

      -- New image-collection relationship table
      CREATE TABLE IF NOT EXISTS mommImageCollections (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        image_id INTEGER NOT NULL,
        collection_id INTEGER NOT NULL,
        added_at DATETIME DEFAULT CURRENT_TIMESTAMP,
        position INTEGER DEFAULT 0,
        FOREIGN KEY (image_id) REFERENCES mommImages(id) ON DELETE CASCADE,
        FOREIGN KEY (collection_id) REFERENCES mommCollections(id) ON DELETE CASCADE,
        UNIQUE(image_id, collection_id)
      );
    `;

    // Execute schema alterations (ignore errors for columns that already exist)
    const alterStatements = [
      ...enhancedSchema.split(';').filter(stmt => stmt.trim()),
      ...enhancedImageSchema.split(';').filter(stmt => stmt.trim()),
      ...enhancedAudioSchema.split(';').filter(stmt => stmt.trim()),
      ...enhancedVideoSchema.split(';').filter(stmt => stmt.trim())
    ];

    for (const statement of alterStatements) {
      if (statement.trim()) {
        try {
          await new Promise((resolve, reject) => {
            db.run(statement.trim(), (err) => {
              if (err && !err.message.includes('duplicate column name')) {
                console.warn(`Warning during migration: ${err.message}`);
              }
              resolve(); // Continue even if column already exists
            });
          });
        } catch (err) {
          console.warn(`Migration warning: ${err.message}`);
        }
      }
    }

    // Create new tables
    await new Promise((resolve, reject) => {
      db.exec(newTables, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });

    // 2. Create indexes for performance
    const indexes = `
      CREATE INDEX IF NOT EXISTS idx_momm_messages_chat_id ON mommMessages(chat_id);
      CREATE INDEX IF NOT EXISTS idx_momm_messages_timestamp ON mommMessages(timestamp);
      CREATE INDEX IF NOT EXISTS idx_momm_images_hash ON mommImages(hash);
      CREATE INDEX IF NOT EXISTS idx_momm_images_chat_id ON mommImages(chat_id);
      CREATE INDEX IF NOT EXISTS idx_momm_images_collected ON mommImages(is_collected);
      CREATE INDEX IF NOT EXISTS idx_momm_audio_hash ON mommAudio(hash);
      CREATE INDEX IF NOT EXISTS idx_momm_videos_hash ON mommVideos(hash);
      CREATE INDEX IF NOT EXISTS idx_momm_collections_name ON mommCollections(name);
      CREATE INDEX IF NOT EXISTS idx_momm_image_collections_image ON mommImageCollections(image_id);
      CREATE INDEX IF NOT EXISTS idx_momm_image_collections_collection ON mommImageCollections(collection_id);
    `;

    await new Promise((resolve, reject) => {
      db.exec(indexes, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });

    // 3. Create triggers for data consistency
    const triggers = `
      CREATE TRIGGER IF NOT EXISTS update_chat_timestamp
        AFTER INSERT ON mommMessages
        BEGIN
          UPDATE mommChats 
          SET updated_at = CURRENT_TIMESTAMP,
              message_count = (
                SELECT COUNT(*) FROM mommMessages WHERE chat_id = NEW.chat_id
              )
          WHERE id = NEW.chat_id;
        END;

      CREATE TRIGGER IF NOT EXISTS update_collection_count
        AFTER INSERT ON mommImageCollections
        BEGIN
          UPDATE mommCollections 
          SET image_count = (
                SELECT COUNT(*) FROM mommImageCollections WHERE collection_id = NEW.collection_id
              ),
              updated_at = CURRENT_TIMESTAMP
          WHERE id = NEW.collection_id;
        END;

      CREATE TRIGGER IF NOT EXISTS decrease_collection_count
        AFTER DELETE ON mommImageCollections
        BEGIN
          UPDATE mommCollections 
          SET image_count = (
                SELECT COUNT(*) FROM mommImageCollections WHERE collection_id = OLD.collection_id
              ),
              updated_at = CURRENT_TIMESTAMP
          WHERE id = OLD.collection_id;
        END;
    `;

    await new Promise((resolve, reject) => {
      db.exec(triggers, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });

    // 4. Create views for common queries
    const views = `
      CREATE VIEW IF NOT EXISTS chat_summary AS
      SELECT 
        c.id,
        c.title,
        c.timestamp,
        c.created_at,
        c.updated_at,
        c.message_count,
        COUNT(DISTINCT i.id) as image_count,
        COUNT(DISTINCT a.id) as audio_count,
        COUNT(DISTINCT v.id) as video_count
      FROM mommChats c
      LEFT JOIN mommMessages m ON c.id = m.chat_id
      LEFT JOIN mommImages i ON m.message_id = i.message_id
      LEFT JOIN mommAudio a ON m.message_id = a.message_id
      LEFT JOIN mommVideos v ON m.message_id = v.message_id
      GROUP BY c.id, c.title, c.timestamp, c.created_at, c.updated_at, c.message_count;

      CREATE VIEW IF NOT EXISTS collection_summary AS
      SELECT 
        c.id,
        c.name,
        c.description,
        c.created_at,
        c.updated_at,
        c.image_count,
        ti.image_data as thumbnail_data
      FROM mommCollections c
      LEFT JOIN mommImages ti ON c.thumbnail_image_id = ti.id;
    `;

    await new Promise((resolve, reject) => {
      db.exec(views, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });

    // 5. Update existing data to set default values
    const dataUpdates = `
      -- Set created_at/updated_at for existing chats
      UPDATE mommChats 
      SET created_at = datetime(timestamp/1000, 'unixepoch'),
          updated_at = datetime(timestamp/1000, 'unixepoch')
      WHERE created_at IS NULL;

      -- Update message counts for existing chats
      UPDATE mommChats 
      SET message_count = (
        SELECT COUNT(*) FROM mommMessages WHERE chat_id = mommChats.id
      )
      WHERE message_count = 0;

      -- Set default values for existing images
      UPDATE mommImages 
      SET is_collected = FALSE,
          tags = '[]',
          metadata = '{}'
      WHERE is_collected IS NULL;
    `;

    const updateStatements = dataUpdates.split(';').filter(stmt => stmt.trim());
    for (const statement of updateStatements) {
      if (statement.trim()) {
        await new Promise((resolve, reject) => {
          db.run(statement.trim(), (err) => {
            if (err) {
              console.warn(`Data update warning: ${err.message}`);
            }
            resolve();
          });
        });
      }
    }

    console.log('✅ Database migration completed successfully');
    return true;

  } catch (error) {
    console.error('❌ Migration failed:', error);
    // Without transactions, we can't rollback, but individual operations should be atomic
    throw error;
  }
};

// Check if migration is needed
export const isMigrationNeeded = async (db) => {
  return new Promise((resolve) => {
    // Always run migration for new/existing databases to ensure all tables and columns exist
    // This is safer than trying to detect what's missing
    resolve(true);
  });
};

// Version tracking for future migrations
export const setDatabaseVersion = async (db, version) => {
  return new Promise((resolve, reject) => {
    // Create version table if it doesn't exist
    db.run(`
      CREATE TABLE IF NOT EXISTS schema_version (
        id INTEGER PRIMARY KEY,
        version TEXT NOT NULL,
        applied_at DATETIME DEFAULT CURRENT_TIMESTAMP
      )
    `, (err) => {
      if (err) {
        reject(err);
        return;
      }
      
      // Insert or update version
      db.run(
        'INSERT OR REPLACE INTO schema_version (id, version) VALUES (1, ?)',
        [version],
        (err) => {
          if (err) reject(err);
          else resolve();
        }
      );
    });
  });
};

export const getDatabaseVersion = async (db) => {
  return new Promise((resolve) => {
    db.get('SELECT version FROM schema_version WHERE id = 1', (err, row) => {
      if (err || !row) {
        resolve('1.0.0'); // Default version if not found
      } else {
        resolve(row.version);
      }
    });
  });
};

// module.exports = {
//   migrateMommDatabase,
//   isMigrationNeeded,
//   setDatabaseVersion,
//   getDatabaseVersion
// };